/**
 * Group Service
 * Handles API communication for group-related endpoints
 */

import type {
  Group,
  GroupListResponse,
  CreateGroupData,
  UpdateGroupData,
  AddMemberData
} from '../types/group'
import { tokenManager } from '../utils/tokenManager'

export interface GroupListParams {
  limit?: number;
  offset?: number;
  page?: number;
  search?: string;
  is_personal?: boolean;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

export interface GroupErrorResponse {
  error: {
    code: string;
    message: string;
    details?: string;
  };
}

export interface GroupInvitationResponse {
  id: number
  type: string
  inviter_user_id: number
  invitee_user_id: number
  resource_id: number
  resource_type: string
  status: 'pending' | 'accepted' | 'declined'
  payload: {
    group_name?: string
    inviter_name?: string
    [key: string]: unknown
  }
  created_at: string
  updated_at: string
}

export class GroupError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data?: GroupErrorResponse
  ) {
    super(message);
    this.name = 'GroupError';
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidationError(): boolean {
    return this.status === 422 || this.status === 400;
  }
}

class GroupService {
  private readonly baseUrl = '/v1/groups';

  /**
   * Helper method to get JWT token for requests
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    try {
      const token = await tokenManager.getAccessToken();
      
      if (!token) {
        throw new GroupError('No access token available', 401);
      }
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      throw new GroupError('Authentication required', 401);
    }
  }

  /**
   * Make HTTP request with authentication
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const authHeaders = await this.getAuthHeaders();
      
      const config: RequestInit = {
        ...options,
        headers: {
          ...authHeaders,
          ...options.headers,
        },
      };

      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as GroupErrorResponse;
        throw new GroupError(
          errorData?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData
        );
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      if (error instanceof GroupError) {
        throw error;
      }
      
      // Network or other errors
      throw new GroupError(
        'Network error occurred. Please check your connection.',
        0,
        { error: { code: 'NETWORK_ERROR', message: String(error) } }
      );
    }
  }

  /**
   * Get a paginated list of groups the user belongs to
   */
  async listGroups(params?: GroupListParams): Promise<GroupListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `?${queryString}` : '';
    
    return this.makeRequest<GroupListResponse>(endpoint, {
      method: 'GET',
    });
  }

  /**
   * Create a new group
   */
  async createGroup(data: CreateGroupData): Promise<Group> {
    return this.makeRequest<Group>('', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get details of a specific group
   */
  async getGroup(id: number): Promise<Group> {
    return this.makeRequest<Group>(`/${id}`, {
      method: 'GET',
    });
  }

  /**
   * Update an existing group
   */
  async updateGroup(id: number, data: UpdateGroupData): Promise<Group> {
    return this.makeRequest<Group>(`/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a group
   */
  async deleteGroup(id: number): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Add a new member to a group
   */
  async addMember(groupId: number, data: AddMemberData): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Remove a member from a group
   */
  async removeMember(groupId: number, userId: number): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/${groupId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Send a group invitation to a user.
   * Only group admins and the owner can call this.
   * POST /v1/groups/:groupId/invitations  { invitee_user_id }
   */
  async sendGroupInvitation(groupId: number, inviteeUserId: number): Promise<GroupInvitationResponse> {
    return this.makeRequest<GroupInvitationResponse>(`/${groupId}/invitations`, {
      method: 'POST',
      body: JSON.stringify({ invitee_user_id: inviteeUserId }),
    });
  }

}

// Export singleton instance
export const groupService = new GroupService();
